<template>
  <article class="group flex items-center gap-3 sm:gap-4 rounded-[24px] bg-white p-3 sm:p-4 border border-[#1c4848]/5 shadow-sm transition-all duration-300 hover:shadow-xl hover:border-[#1c4848]/10">
    <button
      v-if="draggable"
      type="button"
      class="drag-handle flex h-11 w-7 shrink-0 cursor-grab touch-none items-center justify-center rounded-lg text-deen-ink/30 hover:text-deen-ink/70 active:cursor-grabbing"
      :aria-label="`Move: ${item.title}`"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="9" cy="6" r="1.800"/><circle cx="15" cy="6" r="1.800"/><circle cx="9" cy="12" r="1.800"/><circle cx="15" cy="12" r="1.800"/><circle cx="9" cy="18" r="1.800"/><circle cx="15" cy="18" r="1.800"/></svg>
    </button>

    <div class="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-2xl" :class="thumbSurface">
      <img v-if="post.image" :src="post.image" alt="" loading="lazy" class="h-full w-full object-cover">
      <span v-else-if="post.kind === 'ayah'" dir="rtl" lang="ar" class="font-quran text-2xl text-deen-gold" aria-hidden="true">ﷲ</span>
      <span v-else class="font-serif text-4xl leading-none" :class="post.kind === 'hadith' ? 'text-[#c9a95a]' : 'text-deen-gold'" aria-hidden="true">{{ post.kind === 'hadith' ? '“' : (post.title ?? '?').slice(0, 1) }}</span>
    </div>

    <div class="min-w-0 flex-1">
      <p class="text-xs font-semibold text-deen-ink/50">
        <span class="text-[#a0833c]">{{ typeLabel }}</span> · {{ posted }}
        <template v-if="item.likesCount"> · {{ item.likesCount }} {{ item.likesCount === 1 ? 'like' : 'likes' }}</template>
      </p>
      <p class="mt-1 font-medium leading-snug text-deen-ink line-clamp-2">{{ headline }}</p>
      <p v-if="post.reference && post.kind === 'ayah'" class="mt-0.5 text-sm text-deen-ink/55">{{ post.reference }}</p>
      <p v-if="hiddenInApp" class="mt-1.5 flex items-center gap-1.5 text-xs font-medium text-[#8a5a00]">
        <span class="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true"></span>The app does not show this kind of post
      </p>
    </div>

    <div class="flex shrink-0 items-center">
      <template v-if="confirming">
        <button type="button" class="min-h-11 rounded-xl bg-red-700 px-4 text-xs font-bold text-white" @click="$emit('remove')">Remove</button>
        <button type="button" class="min-h-11 px-3 text-xs font-bold text-deen-ink/60" @click="confirming = false">Keep</button>
      </template>
      <button
        v-else
        type="button"
        class="flex h-11 w-11 items-center justify-center rounded-full text-deen-ink/30 transition-colors hover:bg-red-50 hover:text-red-700"
        :aria-label="`Remove from feed: ${item.title}`"
        @click="confirming = true"
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
      </button>
    </div>
  </article>
</template>

<script setup>
import { computed, ref } from 'vue';
import { FEED_TYPE_OPTIONS, TYPES_NOT_IN_APP, phonePostFromFeed } from '@/utils/contentFeedType.js';

const props = defineProps({
  item: { type: Object, required: true },
  draggable: { type: Boolean, default: true },
});

defineEmits(['remove']);

const confirming = ref(false);
const post = computed(() => phonePostFromFeed(props.item));
const hiddenInApp = computed(() => TYPES_NOT_IN_APP.includes(props.item.type));
const typeLabel = computed(() => FEED_TYPE_OPTIONS.find((o) => o.value === props.item.type)?.label ?? props.item.type);
const headline = computed(() => {
  if (post.value.kind === 'page') return post.value.pages.map((page) => page.title).join(' · ') || props.item.title;
  return (post.value.kind === 'ayah' || post.value.kind === 'hadith' ? post.value.text : post.value.title) || props.item.title;
});
const posted = computed(() => (props.item.createdAt ? new Date(props.item.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : ''));
const thumbSurface = computed(() => {
  if (post.value.kind === 'ayah') return 'bg-deen-ink';
  if (post.value.kind === 'hadith') return 'bg-deen-sand';
  return 'bg-deen-ink';
});
</script>
